import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store, Box, CheckCircle, X, ArrowLeft, Zap, Search, ChevronRight, Loader2
} from "lucide-react";

const QuoteForm = () => {
  const navigate = useNavigate();
  const BASEURL = import.meta.env.VITE_BASE_URL;

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [searchBrand, setSearchBrand] = useState("");
  const [searchModel, setSearchModel] = useState("");
  const [brandPage, setBrandPage] = useState(0);
  const [brandTotal, setBrandTotal] = useState(0);
  const [brandHasMore, setBrandHasMore] = useState(false);
  const [loadingMoreBrands, setLoadingMoreBrands] = useState(false);
  const [modelPage, setModelPage] = useState(0);
  const [modelTotal, setModelTotal] = useState(0);
  const [modelHasMore, setModelHasMore] = useState(false);
  const [loadingMoreModels, setLoadingMoreModels] = useState(false);
  const [displayBrand, setDisplayBrand] = useState("");
  const [displayModel, setDisplayModel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const brandSearchTimer = useRef(null);
  const modelSearchTimer = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const imageCache = useRef(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      clearTimeout(brandSearchTimer.current);
      clearTimeout(modelSearchTimer.current);
    };
  }, []);

  const createAbortController = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current;
  };

  const joinUrl = (base, p) => {
    if (!p) return null;
    if (p.startsWith("http")) return p;
    return `${base.replace(/\/+$/, "")}/${p.replace(/^\/+/, "")}`;
  };

  const getFallbackImage = (w = 60, h = 60) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
        <rect width="${w}" height="${h}" fill="#1a1a1a" rx="8"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#FF6B35" font-size="9" font-weight="700">NO IMG</text>
      </svg>`
    )}`;

  const fetchBrands = useCallback(async (page = 0, search = "", append = false) => {
    page === 0 ? setLoading(true) : setLoadingMoreBrands(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/brand/list`, {
        params: { page, limit: 24, search },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const data = Array.isArray(res.data.brands) ? res.data.brands : [];
      const mapped = data.map((b) => ({ id: b.brand_id, name: b.brand_name, image: b.logo ? joinUrl(BASEURL, b.logo) : null }));
      setBrands((prev) => append ? [...prev, ...mapped] : mapped);
      setBrandPage(page);
      setBrandTotal(res.data.total ?? 0);
      setBrandHasMore(res.data.hasMore ?? false);
    } catch (err) {
      if (!isMountedRef.current || axios.isCancel(err) || err.name === "AbortError") return;
      setError("Failed to load brands. Please try again.");
    } finally {
      if (isMountedRef.current) { setLoading(false); setLoadingMoreBrands(false); }
    }
  }, [BASEURL]);

  const fetchModelsByBrand = useCallback(async (brandId, page = 0, search = "", append = false) => {
    page === 0 ? setLoading(true) : setLoadingMoreModels(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/model/${brandId}`, {
        params: { page, limit: 30, search },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const data = Array.isArray(res.data.models) ? res.data.models : [];
      const mapped = data.map((m) => ({ id: m.id, name: m.model_name }));
      setModels((prev) => append ? [...prev, ...mapped] : mapped);
      setModelPage(page);
      setModelTotal(res.data.total ?? 0);
      setModelHasMore(res.data.hasMore ?? false);
      if (page === 0 && mapped.length === 0) setError("No models found for selected brand.");
    } catch (err) {
      if (!isMountedRef.current || axios.isCancel(err) || err.name === "AbortError") return;
      setError("Failed to load models. Please try again.");
    } finally {
      if (isMountedRef.current) { setLoading(false); setLoadingMoreModels(false); }
    }
  }, [BASEURL]);

  const handleBrandSearchChange = (e) => {
    const val = e.target.value;
    setDisplayBrand(val);
    clearTimeout(brandSearchTimer.current);
    brandSearchTimer.current = setTimeout(() => {
      setSearchBrand(val);
      setBrands([]);
      fetchBrands(0, val, false);
    }, 1000);
  };

  const handleModelSearchChange = (e) => {
    const val = e.target.value;
    setDisplayModel(val);
    clearTimeout(modelSearchTimer.current);
    modelSearchTimer.current = setTimeout(() => {
      setSearchModel(val);
      if (selectedBrand) { setModels([]); fetchModelsByBrand(selectedBrand.id, 0, val, false); }
    }, 1000);
  };

  const handleBrandClick = async () => {
    setCurrentStep(1);
    if (brands.length === 0) await fetchBrands(0, "", false);
  };

  const handleBrandSelect = async (brand) => {
    setSelectedBrand(brand);
    setSelectedModel(null);
    setModels([]);
    setModelPage(0);
    setModelHasMore(false);
    setSearchModel("");
    setError(null);
    await fetchModelsByBrand(brand.id, 0, "", false);
    setCurrentStep(2);
  };

  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setError(null);
    setCurrentStep(3);
  };

  const handleCheckPrices = async () => {
    if (!selectedBrand || !selectedModel) { setError("Please select brand and model."); return; }
    setLoading(true);
    setError(null);
    try {
      const controller = createAbortController();
      const res = await axios.get(`${BASEURL}/admin/service/filter`, {
        params: { brand_id: selectedBrand.id, model_id: selectedModel.id },
        headers: { Accept: "application/json" },
        timeout: 15000,
        signal: controller.signal,
      });
      if (!isMountedRef.current) return;
      const services = Array.isArray(res.data) ? res.data : res.data?.services || [];
      if (!services.length) { setError("No services available for this brand/model."); return; }
      navigate("/service-packages", {
        state: {
          bikeData: {
            brand: selectedBrand.name, model: selectedModel.name,
            image: "/images/default-bike.png",
            brandLogo: selectedBrand.image || "",
            brandId: selectedBrand.id, modelId: selectedModel.id, services,
          },
        },
      });
    } catch (err) {
      if (!isMountedRef.current || axios.isCancel(err) || err.name === "AbortError") return;
      setError(err?.response?.data?.message || "Failed to load services. Please try again.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleImageError = (e, imageUrl) => {
    if (!imageCache.current.has(imageUrl)) {
      imageCache.current.add(imageUrl);
      e.currentTarget.src = getFallbackImage(60, 60);
    }
  };

  const steps = [
    { number: 1, title: "Brand", icon: Store },
    { number: 2, title: "Model", icon: Box },
    { number: 3, title: "Confirm", icon: CheckCircle },
  ];

  const slideVariants = {
    enter: { x: 30, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -30, opacity: 0 },
  };

  // ── Sub-components ───────────────────────────────────────────────────────────

  const ErrorBanner = () => !error ? null : (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
      <X className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
      <p className="text-red-400 text-sm">{error}</p>
    </motion.div>
  );

  const LoadMoreBtn = ({ onClick, isLoading, shown, total }) => (
    <button onClick={onClick} disabled={isLoading}
      className="w-full py-3 border border-[#FF6B35]/30 rounded-xl text-sm font-semibold text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all flex items-center justify-center gap-2 disabled:opacity-40 mt-3">
      {isLoading
        ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Loading...</span></>
        : <span>Load More ({shown} of {total})</span>}
    </button>
  );

  const BrandSkeleton = () => (
    <div className="grid grid-cols-3 gap-2.5">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="p-4 rounded-2xl bg-white/5 animate-pulse">
          <div className="w-14 h-14 bg-white/10 rounded-xl mx-auto mb-3" />
          <div className="h-2.5 bg-white/10 rounded mx-auto w-3/4" />
        </div>
      ))}
    </div>
  );

  const ModelSkeleton = () => (
    <div className="grid grid-cols-2 gap-2.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="p-4 rounded-2xl bg-white/5 animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex-shrink-0" />
          <div className="flex-1"><div className="h-2.5 bg-white/10 rounded w-3/4" /></div>
        </div>
      ))}
    </div>
  );

  const ProgressStepper = () => {
    if (currentStep === 0) return null;
    return (
      <div className="px-6 py-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between relative">
          <div className="absolute top-5 left-4 right-4 h-px bg-white/10">
            <motion.div className="h-full bg-[#FF6B35]" initial={{ width: 0 }}
              animate={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }} />
          </div>
          {steps.map((step) => {
            const Icon = step.icon;
            const isCompleted = currentStep > step.number;
            const isCurrent = currentStep === step.number;
            return (
              <div key={step.number} className="flex flex-col items-center flex-1 relative z-10">
                <motion.div
                  animate={isCompleted || isCurrent
                    ? { backgroundColor: "#FF6B35", borderColor: "#FF6B35", scale: 1 }
                    : { backgroundColor: "#1a1a1a", borderColor: "rgba(255,255,255,0.1)", scale: 0.9 }}
                  className="w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors">
                  {isCompleted
                    ? <CheckCircle className="h-5 w-5 text-white" />
                    : <Icon className={`h-4 w-4 ${isCurrent ? "text-white" : "text-gray-600"}`} />}
                </motion.div>
                <span className={`text-xs mt-2 font-semibold ${isCurrent || isCompleted ? "text-[#FF6B35]" : "text-gray-600"}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Step 0: Initial ──────────────────────────────────────────────────────────
  const InitialStep = () => (
    <motion.div variants={slideVariants} initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }} className="space-y-4">
      <div className="text-center pb-2">
        <div className="w-14 h-14 bg-[#FF6B35]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#FF6B35]/20">
          <Zap className="w-7 h-7 text-[#FF6B35]" />
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Find Your Service</h2>
        <p className="text-sm text-gray-500">Select your bike to view available services</p>
      </div>

      <div className="space-y-3">
        <motion.button onClick={handleBrandClick} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
          className="w-full p-4 bg-white/[0.04] border border-white/10 rounded-2xl hover:border-[#FF6B35]/40 hover:bg-[#FF6B35]/5 transition-all group text-left">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#FF6B35] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#FF6B35]/20">
              <Store className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-[#FF6B35] uppercase tracking-wide mb-0.5">Select Brand</p>
              <p className="text-sm text-gray-400">{selectedBrand ? selectedBrand.name : "Choose your bike brand"}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#FF6B35] group-hover:translate-x-0.5 transition-all" />
          </div>
        </motion.button>

        <motion.button onClick={() => selectedBrand && setCurrentStep(2)} disabled={!selectedBrand}
          whileHover={selectedBrand ? { scale: 1.01 } : {}} whileTap={selectedBrand ? { scale: 0.99 } : {}}
          className={`w-full p-4 border rounded-2xl transition-all group text-left
            ${selectedBrand
              ? "bg-white/[0.04] border-white/10 hover:border-[#FF6B35]/40 hover:bg-[#FF6B35]/5 cursor-pointer"
              : "bg-white/[0.02] border-white/[0.04] cursor-not-allowed opacity-40"}`}>
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg
              ${selectedBrand ? "bg-[#FF6B35] shadow-[#FF6B35]/20" : "bg-white/10"}`}>
              <Box className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <p className={`text-xs font-bold uppercase tracking-wide mb-0.5 ${selectedBrand ? "text-[#FF6B35]" : "text-gray-600"}`}>
                Select Model
              </p>
              <p className="text-sm text-gray-400">{selectedModel ? selectedModel.name : "Choose your bike model"}</p>
            </div>
            <ChevronRight className={`h-4 w-4 transition-all ${selectedBrand ? "text-gray-600 group-hover:text-[#FF6B35] group-hover:translate-x-0.5" : "text-gray-700"}`} />
          </div>
        </motion.button>
      </div>

      <AnimatePresence>
        {selectedBrand && selectedModel && (
          <motion.button onClick={handleCheckPrices} disabled={loading}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
            className="w-full p-4 bg-[#FF6B35] text-white rounded-2xl font-bold text-sm hover:bg-[#E85520] transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#FF6B35]/25">
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Loading Services...</span></>
              : <><span>VIEW AVAILABLE SERVICES</span><ChevronRight className="h-4 w-4" /></>}
          </motion.button>
        )}
      </AnimatePresence>
      <ErrorBanner />
    </motion.div>
  );

  // ── Step 1: Brand Selection ──────────────────────────────────────────────────
  const BrandSelectionStep = () => (
    <motion.div variants={slideVariants} initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Choose Brand</h2>
          <p className="text-xs text-gray-500">{brandTotal > 0 ? `${brandTotal} brands available` : "Select from popular brands"}</p>
        </div>
        <button onClick={() => setCurrentStep(0)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10 rounded-xl transition-colors font-semibold">
          <ArrowLeft className="h-3.5 w-3.5" />Back
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
        <input type="text" placeholder="Search brands..."
          value={displayBrand} onChange={handleBrandSearchChange}
          className="w-full pl-10 pr-9 py-3 text-sm bg-white/[0.04] border border-white/10 rounded-xl focus:border-[#FF6B35]/50 focus:outline-none focus:bg-white/[0.06] transition-all text-white placeholder:text-gray-600" />
        {displayBrand && (
          <button onClick={() => { setDisplayBrand(""); fetchBrands(0, "", false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <ErrorBanner />

      {loading ? <BrandSkeleton /> : brands.length === 0 ? (
        <div className="text-center py-12">
          <Search className="h-10 w-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No brands found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2.5 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
            {brands.map((brand, i) => (
              <motion.button key={brand.id} onClick={() => handleBrandSelect(brand)} disabled={loading}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                className="group p-3 bg-white/[0.04] border border-white/[0.08] rounded-2xl hover:border-[#FF6B35]/40 hover:bg-[#FF6B35]/5 transition-all">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/[0.06] rounded-xl p-2 group-hover:bg-white/10 transition-all">
                    <img src={brand.image || getFallbackImage(56, 56)} alt={brand.name}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => handleImageError(e, brand.image)} loading="lazy" />
                  </div>
                  <span className="text-[11px] text-center font-semibold text-gray-300 line-clamp-2 leading-tight group-hover:text-white">
                    {brand.name}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
          {brandHasMore && <LoadMoreBtn onClick={() => fetchBrands(brandPage + 1, searchBrand, true)} isLoading={loadingMoreBrands} shown={brands.length} total={brandTotal} />}
        </>
      )}
    </motion.div>
  );

  // ── Step 2: Model Selection ──────────────────────────────────────────────────
  const ModelSelectionStep = () => (
    <motion.div variants={slideVariants} initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Select Model</h2>
          <p className="text-xs text-gray-500">{selectedBrand?.name}{modelTotal > 0 ? ` • ${modelTotal} models` : ""}</p>
        </div>
        <button onClick={() => { setCurrentStep(1); setSelectedModel(null); }}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10 rounded-xl transition-colors font-semibold">
          <ArrowLeft className="h-3.5 w-3.5" />Back
        </button>
      </div>

      {selectedBrand && (
        <div className="p-3 bg-[#FF6B35]/5 rounded-xl border border-[#FF6B35]/20 flex items-center gap-3">
          <div className="w-12 h-12 bg-white/5 rounded-xl p-1.5 flex items-center justify-center flex-shrink-0">
            <img src={selectedBrand.image || getFallbackImage(48, 48)} alt={selectedBrand.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => handleImageError(e, selectedBrand.image)} />
          </div>
          <div>
            <p className="text-[10px] text-[#FF6B35] font-bold uppercase tracking-wide">Selected Brand</p>
            <p className="text-sm font-bold text-white">{selectedBrand.name}</p>
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
        <input type="text" placeholder="Search models..."
          value={displayModel} onChange={handleModelSearchChange}
          className="w-full pl-10 pr-9 py-3 text-sm bg-white/[0.04] border border-white/10 rounded-xl focus:border-[#FF6B35]/50 focus:outline-none focus:bg-white/[0.06] transition-all text-white placeholder:text-gray-600" />
        {displayModel && (
          <button onClick={() => { setDisplayModel(""); if (selectedBrand) fetchModelsByBrand(selectedBrand.id, 0, "", false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <ErrorBanner />

      {loading ? <ModelSkeleton /> : models.length === 0 ? (
        <div className="text-center py-12">
          <Box className="h-10 w-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No models found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
            {models.map((model, i) => (
              <motion.button key={model.id} onClick={() => handleModelSelect(model)} disabled={loading}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                className="group p-3.5 bg-white/[0.04] border border-white/[0.08] rounded-2xl hover:border-[#FF6B35]/40 hover:bg-[#FF6B35]/5 transition-all text-left">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex-shrink-0 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-[#FF6B35]/10 transition-all">
                    <Box className="h-4 w-4 text-[#FF6B35]" />
                  </div>
                  <span className="text-sm font-semibold text-gray-300 line-clamp-2 group-hover:text-white">{model.name}</span>
                </div>
              </motion.button>
            ))}
          </div>
          {modelHasMore && <LoadMoreBtn onClick={() => fetchModelsByBrand(selectedBrand.id, modelPage + 1, searchModel, true)} isLoading={loadingMoreModels} shown={models.length} total={modelTotal} />}
        </>
      )}
    </motion.div>
  );

  // ── Step 3: Confirmation ─────────────────────────────────────────────────────
  const ConfirmationStep = () => (
    <motion.div variants={slideVariants} initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }} className="space-y-5">
      <div className="text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
          className="w-14 h-14 mx-auto mb-4 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center">
          <CheckCircle className="h-7 w-7 text-green-400" />
        </motion.div>
        <h2 className="text-xl font-bold text-white mb-1">Confirm Selection</h2>
        <p className="text-sm text-gray-500">Review your bike details</p>
      </div>

      <div className="space-y-2.5 bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4">
        <div className="flex items-center gap-3 p-3 bg-white/[0.04] rounded-xl">
          <div className="w-12 h-12 bg-white/5 rounded-xl p-2 flex items-center justify-center flex-shrink-0">
            <img src={selectedBrand?.image || getFallbackImage(48, 48)} alt={selectedBrand?.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => handleImageError(e, selectedBrand?.image)} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] text-[#FF6B35] font-bold uppercase tracking-wide mb-0.5">Brand</p>
            <p className="text-sm font-bold text-white">{selectedBrand?.name}</p>
          </div>
          <button onClick={() => { setCurrentStep(1); setSelectedModel(null); }}
            className="px-2.5 py-1 text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10 rounded-lg transition-colors font-semibold">Edit</button>
        </div>
        <div className="flex items-center gap-3 p-3 bg-white/[0.04] rounded-xl">
          <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Box className="h-6 w-6 text-[#FF6B35]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] text-[#FF6B35] font-bold uppercase tracking-wide mb-0.5">Model</p>
            <p className="text-sm font-bold text-white">{selectedModel?.name}</p>
          </div>
          <button onClick={() => setCurrentStep(2)}
            className="px-2.5 py-1 text-xs text-[#FF6B35] hover:bg-[#FF6B35]/10 rounded-lg transition-colors font-semibold">Edit</button>
        </div>
      </div>

      <ErrorBanner />

      <div className="space-y-2.5">
        <motion.button onClick={handleCheckPrices} disabled={!selectedBrand || !selectedModel || loading}
          whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
          className={`w-full p-4 rounded-2xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg
            ${!selectedBrand || !selectedModel || loading
              ? "bg-white/10 text-gray-500 cursor-not-allowed"
              : "bg-[#FF6B35] text-white hover:bg-[#E85520] shadow-[#FF6B35]/25"}`}>
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Loading Services...</span></>
            : <><span>VIEW AVAILABLE SERVICES</span><ChevronRight className="h-4 w-4" /></>}
        </motion.button>
        <button onClick={() => setCurrentStep(0)}
          className="w-full p-2.5 rounded-xl text-xs text-gray-600 hover:bg-white/[0.04] transition-colors">
          Start Over
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="w-full" id="quote-form">
      <div className="bg-[#111111] rounded-2xl border border-white/[0.08] overflow-hidden shadow-2xl shadow-black/40">
        {/* Header */}
        <div className="p-6 border-b border-white/[0.06] relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/5 to-transparent" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full animate-pulse" />
              <span className="text-xs text-[#FF6B35] font-semibold uppercase tracking-widest">Quick Booking</span>
            </div>
            <h1 className="text-2xl font-black text-white">Book Your Service</h1>
            <p className="text-sm text-gray-500 mt-0.5">Fast & easy bike service booking</p>
          </div>
        </div>

        <ProgressStepper />

        {/* Step Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {currentStep === 0 && <InitialStep key="step0" />}
            {currentStep === 1 && <BrandSelectionStep key="step1" />}
            {currentStep === 2 && <ModelSelectionStep key="step2" />}
            {currentStep === 3 && <ConfirmationStep key="step3" />}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2 border-t border-white/[0.04]">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 bg-green-500 rounded-full" />
              Secure Booking
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1 h-1 bg-[#FF6B35] rounded-full" />
              5000+ Customers
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #FF6B35; opacity: 0.3; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #E85520; }
      `}</style>
    </div>
  );
};

export default QuoteForm;
